import * as THREE from "three";

import {ResetLineMesh} from "./LineControl";
import {EmptyGroup} from "./Common";
import { radToDeg } from "three/src/math/MathUtils";
import {useViewerStore} from "../../../../../store/store";
import {LAYER_OPTIONS} from "../../../../../Constants";
import { Viewer } from "../Viewer-Factory/Viewer";

const transTime = 200;

/**
 * Fit the orthographic camera to show the provided objects in a near top-down view.
 * Useful for "full site" (include map imagery + trees) or "roof only" (hide trees/maps) previews.
 */
export function SetTopFitView(
    self: Viewer,
    opts?: {
        includeRoofs?: boolean;
        includeTrees?: boolean;
        includeObstructions?: boolean;
        includeMapCustom?: boolean;
        padding?: number;
        minZoom?: number;
        maxZoom?: number;
        height?: number;
    }
) {
    if (!self?.camera || !self?.controls) return;
    const includeRoofs = opts?.includeRoofs !== false;
    const includeTrees = !!opts?.includeTrees;
    const includeObstructions = opts?.includeObstructions !== false;
    const includeMapCustom = !!opts?.includeMapCustom;
    const padding = typeof opts?.padding === "number" && Number.isFinite(opts.padding) ? opts.padding : 1.2;
    const minZoom = typeof opts?.minZoom === "number" && Number.isFinite(opts.minZoom) ? opts.minZoom : 1.2;
    const maxZoom = typeof opts?.maxZoom === "number" && Number.isFinite(opts.maxZoom) ? opts.maxZoom : 25;
    const height = typeof opts?.height === "number" && Number.isFinite(opts.height) ? opts.height : 110;

    const objects: THREE.Object3D[] = [];
    if (includeRoofs && self.roofGroup) objects.push(self.roofGroup);
    if (includeTrees && self.treeGroup) objects.push(self.treeGroup);
    if (includeObstructions && self.obstGroup) objects.push(self.obstGroup);
    if (includeMapCustom && (self as any).mapCustom) objects.push((self as any).mapCustom as THREE.Object3D);

    if (objects.length === 0) return;

    const unionBox = new THREE.Box3();
    for (const obj of objects) {
        try {
            const b = new THREE.Box3().setFromObject(obj);
            if (!b.isEmpty()) unionBox.union(b);
        } catch {
            // ignore individual bound failures
        }
    }
    if (unionBox.isEmpty()) return;

    const center = unionBox.getCenter(new THREE.Vector3());
    const size = unionBox.getSize(new THREE.Vector3());
    const maxDimension = Math.max(size.x, size.z) || 1;

    // Orthographic camera: visible world width = camWidth / zoom.
    const camWidth = self.camera.right - self.camera.left;
    let zoom = camWidth / (maxDimension * padding);
    zoom = Math.max(minZoom, Math.min(maxZoom, zoom));

    // Near top-down: limit rotation and center on the site.
    self.controls.maxPolarAngle = 0.05;
    self.camera.position.set(center.x, height, center.z);
    self.controls.target.set(center.x, 0, center.z);
    self.camera.zoom = zoom;
    self.camera.updateProjectionMatrix();
    self.controls.update();
    SetCompassDeg(self.camera, self);
}

export function SetCameraView(self: Viewer, viewMode: '2d' | '3d', home?: boolean) {
    if (self.viewMode === '3d') self.camPos3D = {...self.camera.position};
    var newCamPos;
    if (viewMode === '2d') newCamPos = {x: self.camPos3D.x / 100, y: 100, z: self.camPos3D.z / 100};
    else newCamPos = {x: self.camPos3D.x, y: self.camPos3D.y, z: self.camPos3D.z};


    if(viewMode === '2d') {
        useViewerStore.setState({is2DModeActive: true, is3DModeActive: false});
        self.setVisibleGroup(LAYER_OPTIONS.MAPS.TERRAIN, false);
    }
    else {
        self.setVisibleGroup(LAYER_OPTIONS.MAPS.TERRAIN, true);
        useViewerStore.setState({is2DModeActive: false, is3DModeActive: true});
    }


    // Getting center of roof group
    const boundingBox = new THREE.Box3().setFromObject(self.roofGroup);
    const center = boundingBox.getCenter(new THREE.Vector3());

    if (home) {
        newCamPos = viewMode === '2d' ? {x: center.x, y: 120, z: center.z} : {x: center.x, y: 90, z: center.z + 72};

        self.camera.zoom = GetSiteMidpoint(self).zoom;
        self.camera.updateProjectionMatrix()
        self.controls.update()

        setTimeout(() => {
            SetCompassDeg(self.camera, self);
        }, transTime + 10);
    }

    self.camera.position.set(newCamPos.x, newCamPos.y, newCamPos.z);
    self.controls.target.set(center.x, center.y, center.z);

    self.viewMode = viewMode;
    self.controls.enableRotate = true;
    self.treeGroup.scale.y = viewMode==='2d'?0.1:1;
    self.roofGroup.children.forEach(house=>{
        house.scale.y = viewMode==='2d'? 0.1 : 1
    })


    self.obstGroup.children.forEach(obst => {
        if (viewMode === '2d') obst.scale.y = 0.1
        if (viewMode === '3d') obst.scale.y = 1
    })
    SetCompassDeg(self.camera, self);

    self.setMeasureMode(false);
    self.setDrawMode(false);
    self.setAutoAlignMode(false);
    ResetLineMesh(self, 'stop');
    EmptyGroup(self.lineGroup);
}

export function SetCameraAngle(self: Viewer, rot: number) {
    const {camera, camDis, camPosY} = self;
    const posX = Math.sin(rot) * camDis, posZ = Math.cos(rot) * camDis;
    camera.position.set(posX, camPosY, posZ);
    camera.lookAt(0, 0, 0);
}

export function SetCompassDeg(camera: THREE.OrthographicCamera, self: Viewer) {
    const cameraForward = camera.getWorldDirection(new THREE.Vector3());
    cameraForward.y = 0;

    const forward = new THREE.Vector3(0, 0, 1);
    let angle = cameraForward.angleTo(forward) + Math.PI;

    if (cameraForward.x < 0) {
        angle = 2 * Math.PI - angle;
    }

    const angleDeg = radToDeg(angle);
    self.cameraHeading = angleDeg;

    const compassEle = document.getElementById('compassEle');
    if (compassEle) {
        compassEle.style.transform = `rotate(${angleDeg}deg)`;
    }
}

export function SetInitCamRotVal(self: Viewer) {
    const {autoRotate, compassDrag} = self;
    if (!autoRotate && !compassDrag) return;
    self.controls.target.set(0, 0, 0);
    const {camera} = self, {position} = camera, {x, y, z} = position, deltaRot = x < 0 ? Math.PI : 0;
    self.camDis = Math.sqrt(x * x + z * z);
    self.camPosY = y;
    self.camRot = Math.atan(z / x) + deltaRot;
}

const deltaAngle = 0.002;

export function RotateCamera(self: Viewer) {
    const {camRot, camDis, camPosY, camera} = self;
    if (camera.position.y !== camPosY) {
        SetInitCamRotVal(self);
        return;
    }
    const posX = camDis * Math.cos(camRot), posZ = camDis * Math.sin(camRot);
    camera.position.set(posX, camPosY, posZ);
    camera.lookAt(0, 0, 0);
    SetCompassDeg(camera, self);
    self.camRot += deltaAngle;
}

export function SetTopView(self: Viewer, val?: boolean) {
    const {camera, controls} = self;
    if (val) {
        const {position} = camera, {x, y, z} = position
        self.camPos3D = {x, y, z};
        controls.maxPolarAngle = 0.05;
        self.camera.position.set(x / 100, 70, z / 100);
    } else {
        const {x, y, z} = self.camPos3D;
        controls.maxPolarAngle = Math.PI / 2 - 0.1;
        self.camera.position.set(x, y, z);
    }
    self.controls.target.set(0, 0, 0);
    SetCompassDeg(camera, self);
}

export function SetCameraZoom(self: Viewer, dir: number) {
    self.camera.zoom = Math.max(6, self.camera.zoom - dir);
    self.camera.updateProjectionMatrix()
}

function GetSiteMidpoint(self: Viewer) {
    const positions = [];

    for (const wall of self.wallMeshArr) {
        const arr = wall.geometry.attributes.position.array;
        for (let i = 0, l = arr.length; i < l; i += 3) {
            positions.push(wall.localToWorld(new THREE.Vector3(arr[i], arr[i + 1], arr[i + 2])));
        }
    }

    if (!positions.length) return null;

    let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity;
    positions.forEach(({ x, z }) => {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (z < minZ) minZ = z;
        if (z > maxZ) maxZ = z;
    });

    const camWidth = self.camera.right - self.camera.left;
    const size = Math.max(maxX - minX, maxZ - minZ) || 1;

    return {
        x: (maxX + minX) / 2,
        z: (maxZ + minZ) / 2,
        zoom: (camWidth / size / 2) - 2,
    };
}

export function SetMidpointView (self: Viewer) {
    SetTopView(self, true);

    const midpoint = GetSiteMidpoint(self);

    if (midpoint) {
        self.camera.position.set(midpoint.x, 70, midpoint.z);
        self.controls.target.set(midpoint.x, 0, midpoint.z);
        self.camera.zoom = midpoint.zoom;
    } else {
        self.camera.zoom = 25;
    }

    self.camera.updateProjectionMatrix();
}